import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import compose from "ramda/src/compose";
import mergeRight from "ramda/src/mergeRight";

import { Modal } from "../Modal";
import { Button } from "../../UI/Button";
import { useModalContext } from "../../contexts/ModalContext/context";
import { Grid } from "../../UI/Grid";
import { getControllerTextFieldRender } from "../../helpers/getControllerTextFieldRender";
import { normalize } from "../../helpers/normalize";
import { QUESTIONS_ANSWER_CHAR_LIMIT } from "../../constants/general";
import { pathOrString } from "../../helpers/pathOrString";
import pathOr from "ramda/src/pathOr";
import { getControllerTextAreaRender } from "../../helpers/getControllerTextAreaRender";
import { useSnackbar } from "../../hooks/useSnackbar";
import { TagSelect } from "../TagSelect";
import { RetainAnswerSelect } from "../RetainAnswerSelect";
import { getTag } from "../../helpers/getTag";

const TITLES = {
  edit: "Modify Slider Scale Question",
  new: "Create Slider Scale Question",
};

export interface IProps {
  data: any;
  onHide: any;
  onSave: any;
  showTagColumn?: any;
}

interface RetainAnswer {
  value: boolean,
  label: string,
}

interface IFormData {
  questionText: string;
  retainAnswer: boolean | null | RetainAnswer;
  low: string;
  high: string;
  tag?: {
    value: number;
    label: string;
  };
  disclaimer: string;
}

const LOW_ANSWER_IDX = 0;
const HIGH_ANSWER_IDX = 1;

export const SliderQuestionModal: React.FC<IProps> = ({
  data,
  onHide,
  onSave,
  showTagColumn,
}) => {
  const { showErrorSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const { setIsOpen } = useModalContext();
  const {
    reset,
    formState: { errors },
    control,
    handleSubmit,
  } = useForm<IFormData>();

  const isEdit = typeof data === "object";
  const visible = isEdit || data === true;

  React.useEffect(() => {
    setIsOpen(visible);
  }, [visible]);

  useEffect(() => {
    setLoading(false);
  }, [data]);

  const handleUpdate = async (newData: IFormData) => {
    setLoading(true);

    const tagObj = newData.tag?.value
      ? {
          tag: {
            id: newData.tag.value,
            name: newData.tag.label,
          },
        }
      : { tag: null, clearTag: true };

    const answers = isEdit
      ? [
          {
            id: pathOrString(["answers", LOW_ANSWER_IDX, "id"], data),
            answerText: newData.low,
            answerOrder: LOW_ANSWER_IDX,
          },
          {
            id: pathOrString(["answers", HIGH_ANSWER_IDX, "id"], data),
            answerText: newData.high,
            answerOrder: HIGH_ANSWER_IDX,
          },
        ]
      : [
          { answerText: newData.low, answerOrder: LOW_ANSWER_IDX },
          { answerText: newData.high, answerOrder: HIGH_ANSWER_IDX },
        ];
    const getRetainAnswerValue = () => {
      if (newData.retainAnswer !== null && newData.retainAnswer !== undefined) {
        // Handle both boolean and object formats
        if (typeof newData.retainAnswer === 'boolean') {
          return newData.retainAnswer;
        }
        const retainAnswer = newData.retainAnswer as RetainAnswer;
        return retainAnswer.value;
      }
      return true;
    };

    const questionData = {
      ...(isEdit ? data : {}),
      ...tagObj,
      questionType: "SLIDER",
      numberOfSliderOptions: isEdit ? data.numberOfSliderOptions || 5 : 5,
      questionText: newData.questionText,
      answers,
      disclaimer: newData.disclaimer,
      disclaimerText: newData.disclaimer,
      retainAnswer: getRetainAnswerValue(),
    };

    const { success, error } = await onSave(questionData);

    if (success) {
      onHide();
    } else {
      showErrorSnackbar(error);
    }
    setLoading(false);
  };

  const getDefaultInputProps = mergeRight({ errors });

  const getTextControllerRender = React.useCallback(
    // @ts-ignore
    compose(getControllerTextFieldRender, getDefaultInputProps),
    []
  );

  const getTextAreaControllerRender = React.useCallback(
    // @ts-ignore
    compose(getControllerTextAreaRender, getDefaultInputProps),
    []
  );

  useEffect(() => {
    reset({
      questionText: pathOrString(["questionText"], data),
      low: pathOrString(["answers", LOW_ANSWER_IDX, "answerText"], data),
      high: pathOrString(["answers", HIGH_ANSWER_IDX, "answerText"], data),
      disclaimer: pathOrString(["disclaimer"], data),
      tag: getTag(data) as any,
      retainAnswer: pathOr(true, ["retainAnswer"], data),
    });
  }, [normalize(data)]);

  return (
    <Modal
      maxWidth="md"
      onClose={onHide}
      title={isEdit ? TITLES.edit : TITLES.new}
      containerSx={{
        overflow: "visible",
      }}
      contentSx={{
        overflow: "visible",
      }}
      actions={
        <>
          <Button appearance="subtle" onClick={() => onHide()}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSubmit(handleUpdate)}
            isLoading={isLoading}
          >
            Save Changes
          </Button>
        </>
      }
    >
      <Grid container spacing={2} px={3} py={2} width={658}>
        <Grid item xs={12}>
          <Controller
            name="questionText"
            control={control}
            // @ts-ignore
            render={getTextControllerRender({
              label: "Question",
              required: true,
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="low"
            control={control}
            // @ts-ignore
            render={getTextControllerRender({
              label: "Low",
              maxLength: QUESTIONS_ANSWER_CHAR_LIMIT,
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="high"
            control={control}
            // @ts-ignore
            render={getTextControllerRender({
              label: "High",
              maxLength: QUESTIONS_ANSWER_CHAR_LIMIT,
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="disclaimer"
            control={control}
            // @ts-ignore
            render={getTextAreaControllerRender({
              label: "Disclaimer (optional)",
              maxLength: 1000,
              minimumRows: 5,
            })}
          />
        </Grid>
        {showTagColumn && (
          <Grid item xs={12}>
            <Controller
              name="tag"
              control={control}
              render={({ field }: any) => (
                <TagSelect {...field} label="Tag (optional)" />
              )}
            />
          </Grid>
        )}

      <Grid item xs={12}>
        <Controller
          name="retainAnswer"
          control={control}
          render={({ field }: any) => (
            <RetainAnswerSelect {...field} label="Retain Answer" />
          )}
        />
      </Grid>
      </Grid>

    </Modal>
  );
};
